import {
  call, put, select, takeLatest, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findWithCodes,
  findWithReadStatus,
  wildcardFilter,
  createOrderByParams,
  successAndErrorsOnDestroy, isEmpty,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import urls, { codeUrl } from '@/constants/urls';
import errorCodes from '@/constants/errors';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import selectors from '@/selectors';
import downloadHelper from '@/utils/downloads';
import { fetchMetrics } from '@/metrics/actions';
import { configurationCodes } from '@/configurations/constants';
import CataloguesService from '../catalogue/service';
import DictionariesService from '../dictionaries/service';
import DatasourceService from '../datasource/service';
import { fetchDatasourceSettingsSuccess, removeDatasourceSettings } from '../datasource/actions';
import hasDatasourceChanged from '../datasource/helper';
import DatasetsService from '../service';
import {
  showDatasetsResults,
  showDatasetsError,
  showDataset,
  showDatasetError,
  newDataset,
  newDatasetError,
  updateDatasetSuccess,
  updateDatasetError,
  destroyDataset,
  destroyDatasetError,
  convertToDictionary,
  convertToDictionaryError,
  convertToDictionarySuccess,
  newDictionaryError,
  newDictionarySuccess,
  showNewDataset,
  showCloneDataset,
  updateDatasetCatalogueSuccess,
  downloadDatasetError,
} from '../actions';
import actions, { datasetLists, datasetSourceEntities } from '../constants';
import DatasetAttachmentsService from '../attachments/service';

function fetchDatasets(
  filter,
  privateFlag,
  internalFlag,
  publicFlag,
  dataModelCode,
  page,
  pageSize,
  requestable,
  queryable,
  orderBy,
) {
  return DatasetsService.datasets(
    filter,
    privateFlag,
    internalFlag,
    publicFlag,
    dataModelCode,
    page,
    pageSize,
    requestable,
    queryable,
    orderBy,
  )
    .then((response) => ({ response }))
    .catch((error) => ({ error }));
}

export function* datasets(action) {
  const items = yield select(selectors.datasets);
  const {
    currentPage,
    dataModelCode,
    display,
    filter,
    internalFlag,
    orderBy,
    pageSize,
    privateFlag,
    publicFlag,
    queryableFlag,
    requestableFlag,
  } = items[action.category];
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePrivate = action.privateFlag || privateFlag;
  const includeInternal = action.internalFlag || internalFlag;
  const includePublic = action.publicFlag || publicFlag;
  const dataModel = action.dataModelCode || dataModelCode;
  const orderByParams = createOrderByParams(orderBy);
  const { response, error } = yield call(
    fetchDatasets,
    wildcardFilter(filter),
    includePrivate,
    includeInternal,
    includePublic,
    dataModel,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    requestableFlag,
    queryableFlag,
    orderByParams,
  );

  if (error) {
    yield put(showDatasetsError(action.category, error));
  } else {
    yield put(showDatasetsResults(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* dataset({ category, code }) {
  const { response, error } = yield call(callAdapter, DatasetsService.dataset(code));
  if (error) {
    yield put(showDatasetError(category, code, error));
  } else {
    yield put(showDataset(category, code, response));
  }
}

function* datasourceSettings(category, code, ds) {
  const { response } = yield call(callAdapter, DatasourceService.datasourceSettings(ds.code));
  if (response) {
    response.source_exists = false;
    yield put(fetchDatasourceSettingsSuccess(category, code, response, true));
  }

  const templates = yield select(selectors.catalogueTemplates);
  const template = findResult(templates, category, ds.settings.catalogue_definition_code);
  yield put(showCloneDataset(category, code, ds, template));
}

function* cloneDataset(category, code, datasetSourceCode) {
  const { response, error } = yield call(callAdapter, DatasetsService.dataset(datasetSourceCode));

  if (error) {
    yield put(showDatasetError(category, code, error));
  } else if (!response.permissions.can_update) {
    yield put(showDatasetError(category, code, { status: errorCodes.forbidden }));
  } else {
    yield call(datasourceSettings, category, code, response);
  }
}

export function* newDatasetFromSource({
  category,
  code,
  datasetSourceType,
  datasetSourceCode,
}) {
  if (!datasetSourceCode) {
    const configurations = yield select(selectors.configurations);
    const { value: { value } } = findResult(configurations, 'all', configurationCodes.DEFAULT_CATALOGUE);
    const templates = yield select(selectors.catalogueTemplates);
    const template = findResult(templates, category, value);
    yield put(showNewDataset(category, code, value, template));
  } else if (datasetSourceType === datasetSourceEntities.TEMPLATE) {
    const templateCode = decodeURIComponent(datasetSourceCode);
    const templates = yield select(selectors.catalogueTemplates);
    const template = findResult(templates, category, templateCode);
    yield put(showNewDataset(category, code, templateCode, template));
  } else if (datasetSourceType === datasetSourceEntities.DATASET) {
    yield call(cloneDataset, category, code, decodeURIComponent(datasetSourceCode));
  }
}

export function* destroy({ items }) {
  const results = yield all(items.map((i) => callAdapterResponseError(i, DatasetsService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Dataset not found');

  if (errors.length > 0) {
    yield put(destroyDatasetError(errors));
  }
  if (success.length > 0) {
    const datasetItems = yield select(selectors.datasets);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(datasetItems, datasetLists, codes);
    const toRefresh = findWithReadStatus(datasetItems, categories, states.SUCCESS);

    yield put(destroyDataset(success, false));
    if (toRefresh) {
      yield all(toRefresh.map((category) => datasets({ category })));
    }
    yield put(fetchMetrics(['datasets']));
  }
}

function* afterCreateDataset(category, code, response, template, previousUrl, silent) {
  const { permissions } = yield select(selectors.role);
  const items = yield select(selectors.datasets);
  const toRefresh = findWithReadStatus(items, datasetLists, states.SUCCESS);

  yield put(newDataset(category, code, previousUrl, response, template, permissions, silent));
  yield all(toRefresh.map((c) => datasets({ category: c })));
  yield put(fetchMetrics(['datasets']));
}

function* saveDatasourceSettings(category, code, response, template, previousUrl, datasource) {
  const item = { ...datasource, code: response.code };
  const { error } = yield call(callAdapter, DatasourceService.updateDatasetDatasource(item));

  yield put(removeDatasourceSettings(category, datasource.code, response.catalogue.title, error));
  yield afterCreateDataset(category, code, response, template, previousUrl, !isEmpty(error));
}

function* createDataset(category, code, ds, template) {
  const { response, error } = yield call(callAdapter, DatasetsService.create(ds));

  if (error) {
    yield put(newDatasetError(category, code, error));
  } else {
    const datasources = yield select(selectors.datasourceSettings);
    const datasource = findResult(datasources, category, code);
    const previousUrl = ds.steps.sourceUrl || codeUrl(urls.DATASET, code);

    if (datasource && hasDatasourceChanged(datasource, datasource.copy)) {
      yield saveDatasourceSettings(category, code, response, template, previousUrl, datasource.copy);
    } else {
      yield afterCreateDataset(category, code, response, template, previousUrl, false);
    }
  }
}

export function* create({ category, code, template }) {
  const items = yield select(selectors.datasets);
  yield call(createDataset, category, code, findResult(items, category, code), template);
}

export function* createFromActive() {
  const ds = yield select(selectors.activeDataset);
  yield call(createDataset, ds.category, ds.code, ds, ds.template);
}

export function* update({ code, properties, template }) {
  const { response, error } = yield call(callAdapter, DatasetsService.update(code, properties));
  if (error) {
    yield put(updateDatasetError('open', code, error));
  } else {
    const { permissions } = yield select(selectors.role);
    yield put(updateDatasetSuccess(code, response, template, permissions));
    yield put(fetchMetrics(['datasets']));
  }
}

export function* updateDatasetCatalogue({ code, catalogue, template }) {
  const { response, error } = yield call(callAdapter, DatasetsService.update(code, { catalogue }));
  if (error) {
    yield put(updateDatasetError('open', code, error));
  } else {
    const { permissions } = yield select(selectors.role);
    yield put(updateDatasetCatalogueSuccess(code, response, template, permissions));
    yield put(fetchMetrics(['datasets']));
  }
}

export function* createDictionaryAndConvert({
  category,
  code,
  convertProps,
  attachmentId,
  dictionaryProps,
}) {
  const { response, error } = yield call(callAdapter, DatasetsService.update(code, dictionaryProps));
  const newDictionaryName = dictionaryProps.dictionaries[0].name;

  if (error) {
    yield put(newDictionaryError(category, code, error));
  } else {
    yield put(newDictionarySuccess(category, code, response, newDictionaryName));
    yield put(fetchMetrics(['datasets']));
    const findTheDictionaryCode = response.dictionaries.find(
      (dictionary) => dictionary.name === newDictionaryName,
    );
    const properties = {
      ...convertProps,
      dictionaryCode: findTheDictionaryCode.code,
      dictionaryName: newDictionaryName,
    };
    yield put(convertToDictionary(category, code, attachmentId, properties, true));
  }
}

export function* convertAttachmentToDictionary({
  category,
  code,
  attachmentId,
  isNew,
  properties,
}) {
  const { error } = yield call(callAdapter, DatasetAttachmentsService.ingest(code, attachmentId, properties));
  if (error) {
    yield put(convertToDictionaryError(properties.dictionaryName, isNew));
  } else {
    yield put(convertToDictionarySuccess(
      category,
      code,
      attachmentId,
      properties.dictionaryName,
      isNew,
      !!properties.deleteSource,
    ));
  }
}

export function* refreshDatasetsPresentation(action) {
  yield call(datasets, { ...action, page: 1, append: false });
}

function* downloadWithDictionaries(code, catalogue) {
  const { response, error } = yield call(callAdapter, DictionariesService.dictionaries(code, 1, null));
  if (error) {
    yield put(downloadDatasetError(code, error));
  } else {
    downloadHelper.jsonDownload({ catalogue, dictionaries: response.items }, `${code}_dataset.json`);
  }
}

export function* download({ code }) {
  const { response, error } = yield call(callAdapter, CataloguesService.catalogue(code));
  if (error) {
    yield put(downloadDatasetError(code, error));
  } else {
    yield call(downloadWithDictionaries, code, response);
  }
}

export default function* datasetsApi() {
  yield takeEvery(
    [
      actions.FETCH_DATASETS,
      actions.REFRESH_DATASETS,
      actions.UPDATE_DATASETS_FLAGS,
      actions.UPDATE_DATASETS_DATA_MODEL_FILTER,
      actions.TOGGLE_DATASET_COLUMN_ORDER_BY,
      actions.TOGGLE_DATASET_SINGLE_SORTING,
    ],
    datasets,
  );
  yield takeEvery(actions.FETCH_DATASET, dataset);
  yield takeEvery(actions.NEW_DATASET_FROM_SOURCE, newDatasetFromSource);
  yield takeEvery(actions.CREATE_DATASET, create);
  yield takeLatest(actions.CREATE_ACTIVE_DATASET, createFromActive);
  yield takeEvery(actions.UPDATE_DATASET, update);
  yield takeLatest(actions.UPDATE_DATASET_CATALOGUE, updateDatasetCatalogue);
  yield takeEvery(actions.DELETE_DATASET, destroy);
  yield takeEvery(actions.CONVERT_TO_DICTIONARY, convertAttachmentToDictionary);
  yield takeEvery(actions.CREATE_AND_CONVERT_TO_DICTIONARY, createDictionaryAndConvert);
  yield takeEvery(actions.TOGGLE_DATASETS_PRESENTATION, refreshDatasetsPresentation);
  yield takeEvery(actions.DOWNLOAD_DATASET, download);
}
